/**
 * Look up EMS display data by Safety ID via the backend API.
 * Backend requires X-API-Key header.
 *
 * API response shape: { success, message, data, timestamp }
 * The endpoint only returns fields that should be shown. If a field is absent, do not show it.
 * There are no "show to EMS" flags in the response — presence in the response means "show it".
 * All EMS fields (firstName, lastName, fullName, profilePictureUrl, emergencyContacts,
 * companiesWithCustomFields, dob, gender, notesToEms, heightFeet, heightInches, weight) are on data.
 *
 * CORS: Backend must allow the header in preflight:
 *   Access-Control-Allow-Headers: Content-Type, X-API-Key
 */

const EMS_API_KEY = process.env.REACT_APP_SCANIFHURT_API_KEY;

/**
 * API base URL for EMS lookup.
 * When the app runs on localhost, use REACT_APP_SUPABASE_LOCALHOSTAPI_URL so the local backend
 * (which allows localhost CORS) is used instead of dev API (which may block localhost).
 * Otherwise use REACT_APP_EMS_API_URL (dev/prod).
 */
export function getEmsApiBase() {
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  const localUrl = process.env.REACT_APP_SUPABASE_LOCALHOSTAPI_URL;
  const base =
    isLocalhost && localUrl ? localUrl : process.env.REACT_APP_EMS_API_URL || "";
  return (base || "").replace(/\/$/, "");
}

function calculateAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

/** Normalize backend payload (snake_case or nested) to riderInfo shape expected by EMSAccess */
function normalizeToRiderInfo(payload) {
  if (!payload || typeof payload !== "object") return null;
  const get = (obj, ...keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null) return v;
    }
    return undefined;
  };
  const first = get(payload, "first_name", "firstName") ?? "";
  const last = get(payload, "last_name", "lastName") ?? "";
  const fullName = get(payload, "fullName") ?? ([first, last].filter(Boolean).join(" ").trim() || "Unknown");
  const rawContacts = get(payload, "emergency_contacts", "emergencyContacts");
  const emergencyContacts = Array.isArray(rawContacts)
    ? rawContacts.map((c) => ({
      firstName: c.first_name ?? c.firstName ?? "",
      lastName: c.last_name ?? c.lastName ?? "",
      relationship: c.relationship ?? "",
      phone: c.phone_number ?? c.phone ?? "",
      showToEMS: c.show_to_ems !== false && c.showToEMS !== false,
      knowsUserPin: Boolean(c.knows_user_pin ?? c.knowsUserPin),
      knowsCompanyPin: Boolean(c.knows_company_pin ?? c.knowsCompanyPin),
      companyId: c.company_id ?? c.companyId ?? "",
      companyName: c.company_name ?? c.companyName ?? "",
    }))
    : [];

  const rawCompanies = get(payload, "companiesWithCustomFields", "companies_with_custom_fields");
  const companiesWithCustomFields = Array.isArray(rawCompanies)
    ? rawCompanies.map((co) => ({
      companyId: co.companyId ?? co.company_id ?? "",
      companyName: co.companyName ?? co.company_name ?? "Unknown",
      customFields: (Array.isArray(co.customFields) ? co.customFields : Array.isArray(co.custom_fields) ? co.custom_fields : []).map((f) => ({
        name: f.name ?? f.field_name ?? "",
        value: f.value ?? f.field_value ?? "",
      })),
    }))
    : [];

  const dob = get(payload, "dob") ?? "";
  const age = payload.age != null ? payload.age : (dob ? calculateAge(dob) : null);

  return {
    firstName: first,
    lastName: last,
    fullName,
    dob,
    age,
    class: get(payload, "class") ?? "",
    imageUrl: get(payload, "profilePictureUrl", "profilePictureSignedUrl", "profile_picture_url", "image_url", "imageUrl", "picture", "profile_image_url") ?? "",
    gender: get(payload, "gender") ?? "",
    heightFeet: payload.height_feet ?? payload.heightFeet ?? "",
    heightInches: payload.height_inches ?? payload.heightInches ?? "",
    weight: payload.weight != null ? payload.weight : "",
    notesToEMS: get(payload, "notes_to_ems", "notesToEMS", "notesToEms") ?? "",
    emergencyContacts,
    companiesWithCustomFields,
    showAdditionalButton: true,
    showDobToEMS: get(payload, "show_to_ems_dob", "showDobToEMS") !== false,
    showGenderToEMS: get(payload, "show_to_ems_gender", "showGenderToEMS") !== false,
    showHeightToEMS: get(payload, "show_to_ems_height", "showHeightToEMS") !== false,
    showWeightToEMS: get(payload, "show_to_ems_weight", "showWeightToEMS") !== false,
    showNotesToEMSToEMS: get(payload, "show_to_ems_notes", "showNotesToEMSToEMS") !== false,
    showClassToEMS: get(payload, "show_to_ems_class", "showClassToEMS") !== false,
  };
}

/**
 * The participant-flow URL the backend gives for a wristband that is not
 * active yet (WRISTBAND_UNCLAIMED) or no longer (WRISTBAND_EXPIRED). Only an
 * http(s) URL is followed. The QR code's own &e= (organizer edition) is passed on.
 */
function wristbandRedirectUrl(data) {
  const code = data && typeof data === "object" ? data.code : null;
  if (code !== "WRISTBAND_UNCLAIMED" && code !== "WRISTBAND_EXPIRED") return null;
  const raw = data.wristband && data.wristband.redirectUrl;
  if (typeof raw !== "string") return null;
  try {
    const target = new URL(raw);
    if (target.protocol !== "https:" && target.protocol !== "http:") return null;
    const edition = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("e") : null;
    if (edition && !target.searchParams.has("e")) target.searchParams.set("e", edition);
    return target.toString();
  } catch (_) {
    return null;
  }
}

function extractBackendMessage(data) {
  if (!data || typeof data !== "object") return "";
  const msg = data.message ?? data.error ?? "";
  return typeof msg === "string" ? msg.trim() : "";
}

/**
 * @param {string} safetyId - Safety ID code (e.g. "10745", "DAN007"), will be uppercased
 * @returns {Promise<{ riderInfo: object, userId?: string, apiError?: string, status?: number } | null>}
 */
export async function lookupSafetyIdByApi(safetyId) {
  const apiBaseRaw = getEmsApiBase();
  if (!apiBaseRaw || !(safetyId || '').trim()) return null;
  const code = (safetyId || '').trim().toUpperCase();
  if (!code) return null;

  const apiBase = apiBaseRaw.replace(/\/$/, '');
  const url = `${apiBase}/ems-lookup/${encodeURIComponent(code)}`;
  const headers = {};
  if (EMS_API_KEY) {
    headers["X-API-Key"] = EMS_API_KEY;
  } else {
    console.warn("[EMS API] REACT_APP_SCANIFHURT_API_KEY not set; backend may return 401.");
  }

  try {
    const res = await fetch(url, { headers: Object.keys(headers).length ? headers : undefined });
    const contentType = res.headers.get("content-type");
    let data = null;
    if (contentType && contentType.includes("application/json")) {
      try {
        data = await res.json();
      } catch (e) {
        console.warn("[EMS API] Failed to parse JSON body (often CORS)", e);
        return { riderInfo: null, apiError: "parse" };
      }
    }
    if (!res.ok) {
      const backendMessage = extractBackendMessage(data);
      if (res.status === 0) {
        console.warn("[EMS API] Request blocked or failed (status 0 — often CORS or network).");
        return { riderInfo: null, apiError: "cors" };
      }
      if (res.status === 401) {
        console.warn("[EMS API] 401 Unauthorized — check REACT_APP_SCANIFHURT_API_KEY in .env matches the backend key exactly.");
      }
      // An unclaimed or expired wristband: the backend says where its scan
      // should go (registration, or the "coverage has expired" page).
      const wristbandRedirect = wristbandRedirectUrl(data);
      if (wristbandRedirect) {
        return { riderInfo: null, apiError: "wristband", status: res.status, message: backendMessage, redirectUrl: wristbandRedirect };
      }
      console.warn("[EMS API] Lookup failed:", res.status, res.statusText, url, data != null ? data : "(no body)");
      return { riderInfo: null, apiError: "http", status: res.status, message: backendMessage };
    }
    if (!data || typeof data !== "object") {
      console.warn("[EMS API] Response not an object:", url, data);
      return null;
    }
    // Backend returns { success, message, data, timestamp }; EMS payload is in data
    const raw = data.data ?? data.riderInfo ?? data;
    // If payload is nested (user + ems_profile), flatten for normalizer and preserve top-level raw fields (e.g. profilePictureUrl, notes_to_ems)
    const payload = raw?.user || raw?.ems_profile
      ? {
        ...(raw.user || {}),
        ...(raw.ems_profile || {}),
        emergency_contacts: raw.emergency_contacts ?? raw.emergencyContacts,
        companiesWithCustomFields: raw.companiesWithCustomFields ?? raw.companies_with_custom_fields,
        profilePictureUrl: raw.profilePictureUrl ?? raw.profilePictureSignedUrl ?? raw.profile_picture_url ?? raw.image_url,
        notes_to_ems: raw.notes_to_ems ?? raw.notesToEMS ?? raw.notesToEms,
      }
      : raw;
    const riderInfo = normalizeToRiderInfo(payload);
    const userId = data.userId ?? raw?.user_id ?? raw?.userId ?? null;
    if (!riderInfo || (!riderInfo.fullName && !riderInfo.firstName)) {
      console.warn("[EMS API] Response missing name fields. Top-level keys:", data ? Object.keys(data) : [], "data keys:", raw ? Object.keys(raw) : []);
      return null;
    }
    // Use response.data.profilePictureUrl or profilePictureSignedUrl (backend sends both)
    const profilePictureUrl =
      data.data?.profilePictureUrl ?? data.data?.profilePictureSignedUrl ?? data.profilePictureUrl ?? data.profilePictureSignedUrl;
    riderInfo.imageUrl =
      typeof profilePictureUrl === "string" && profilePictureUrl.trim() !== ""
        ? profilePictureUrl.trim()
        : "";
    // Message to EMS: read from response.data (payload) under any common key so the main card always shows it when present
    const pickNotes = (o) => {
      if (!o || typeof o !== "object") return "";
      const v = o.notesToEMS ?? o.notes_to_ems ?? o.notesToEms ?? o.ems_notes ?? o.notes;
      return typeof v === "string" ? v.trim() : "";
    };
    const notesFromPayload = pickNotes(data.data) || pickNotes(data) || pickNotes(payload) || pickNotes(raw) || "";
    if (notesFromPayload !== "") {
      riderInfo.notesToEMS = notesFromPayload;
    }
    // Backend only includes fields that should be shown; no "show to EMS" flags in response. Set visibility from presence.
    riderInfo.showDobToEMS = riderInfo.dob != null && String(riderInfo.dob).trim() !== "";
    riderInfo.showGenderToEMS = riderInfo.gender != null && String(riderInfo.gender).trim() !== "";
    riderInfo.showHeightToEMS = (riderInfo.heightFeet != null && riderInfo.heightFeet !== "") || (riderInfo.heightInches != null && riderInfo.heightInches !== "");
    riderInfo.showWeightToEMS = riderInfo.weight != null && String(riderInfo.weight).trim() !== "";
    riderInfo.showNotesToEMSToEMS = riderInfo.notesToEMS != null && String(riderInfo.notesToEMS).trim() !== "";
    riderInfo.showClassToEMS = (riderInfo.class != null && String(riderInfo.class).trim() !== "") || (riderInfo.companiesWithCustomFields && riderInfo.companiesWithCustomFields.length > 0);
    riderInfo.emergencyContacts = (riderInfo.emergencyContacts || []).map((c) => ({ ...c, showToEMS: true }));
    // Docs/Notes and PIN flags from backend (for PIN entry and Docs/Notes buttons)
    const src = raw ?? data?.data ?? data ?? {};
    riderInfo.hasDocuments = src.hasDocuments === true;
    riderInfo.hasNotes = src.hasNotes === true;
    riderInfo.requiresPinForDocs = src.requiresPinForDocs === true;
    riderInfo.requiresPinForNotes = src.requiresPinForNotes === true;
    return { riderInfo, userId };
  } catch (e) {
    console.warn("[EMS API] Request error:", url, e?.message ?? e);
    return { riderInfo: null, apiError: "network" };
  }
}

/** Build headers for EMS API (X-API-Key). Optionally add X-EMS-Access-Token for docs/notes. */
function emsHeaders(token = null) {
  const h = {};
  if (EMS_API_KEY) h["X-API-Key"] = EMS_API_KEY;
  if (token) h["X-EMS-Access-Token"] = token;
  return h;
}

/**
 * Validate PIN for EMS access to documents or notes.
 * @param {string} safetyId - Safety ID code (e.g. "10745")
 * @param {string} pin - PIN entered by user (use "" when PIN not required)
 * @param {"documents"|"notes"} type - "documents" or "notes"
 * @returns {Promise<{ valid: boolean, token?: string, message?: string, reason?: string, apiError?: string, status?: number }>}
 */
export async function validateEmsPin(safetyId, pin, type) {
  const apiBase = getEmsApiBase();
  if (!apiBase || !(safetyId || "").trim()) return { valid: false, apiError: "config" };
  const code = (safetyId || "").trim().toUpperCase();
  const url = `${apiBase}/ems-lookup/${encodeURIComponent(code)}/ems-access/validate-pin`;
  const body = { pin: String(pin ?? "").trim(), type };
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...emsHeaders() },
      body: JSON.stringify(body),
    });
    let data = null;
    try {
      const text = await res.text();
      if (text) data = JSON.parse(text);
    } catch (_) { }
    if (!res.ok) {
      if (res.status === 403) return { valid: false, apiError: "forbidden", status: 403 };
      let message = data?.message ?? null;
      if (!message && typeof data?.error === "string") {
        try {
          const errArr = JSON.parse(data.error);
          if (Array.isArray(errArr) && errArr.length > 0) {
            message = errArr.map((e) => e.message || e.msg).filter(Boolean).join(" ") || data.error;
          } else {
            message = data.error;
          }
        } catch (_) {
          message = data.error;
        }
      }
      if (!message && data?.errors) {
        if (Array.isArray(data.errors)) message = data.errors.join(", ");
        else if (typeof data.errors === "object") {
          const parts = [];
          for (const [k, v] of Object.entries(data.errors)) {
            const arr = Array.isArray(v) ? v : [v];
            parts.push(...arr.map((s) => (s && typeof s === "string" ? s : `${k}: invalid`)));
          }
          message = parts.length ? parts.join(" ") : null;
        }
      }
      if (res.status === 422) {
        console.warn("[EMS API] validate-pin 422:", data);
        return { valid: false, apiError: "validation", status: 422, message: message || "Request validation failed." };
      }
      return { valid: false, apiError: "http", status: res.status, message: message };
    }
    const valid = data?.data?.valid === true;
    const token = data?.data?.token ?? null;
    const message = data?.data?.message ?? data?.message ?? undefined;
    const reason = data?.data?.reason ?? undefined;
    if (process.env.NODE_ENV === "development") {
      console.info("[EMS validate-pin] type=%s response:", type, { valid, token: token ? "(present)" : null, reason, message, rawData: data?.data });
    }
    return {
      valid,
      token: valid && token ? token : undefined,
      ...(message && { message: typeof message === "string" ? message : String(message) }),
      ...(reason && { reason: typeof reason === "string" ? reason : String(reason) }),
    };
  } catch (e) {
    console.warn("[EMS API] validate-pin error:", e?.message ?? e);
    return { valid: false, apiError: "network" };
  }
}

/**
 * Get view/download URL from a document object. Checks common backend field names and resolves relative URLs.
 * @param {object} doc - Document from documents list
 * @param {string} apiBase - API base URL (e.g. from getEmsApiBase()) to resolve relative paths
 * @returns {string|null}
 */
export function getDocumentViewUrl(doc, apiBase) {
  if (!doc || typeof doc !== "object") return null;
  const raw =
    doc.viewUrl ??
    doc.downloadUrl ??
    doc.url ??
    doc.signedUrl ??
    doc.view_url ??
    doc.download_url ??
    doc.signed_url ??
    doc.fileUrl ??
    null;
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  if (apiBase && trimmed.startsWith("/")) {
    const base = apiBase.replace(/\/$/, "");
    return base + trimmed;
  }
  return trimmed;
}

/**
 * Fetch a view URL for a single document (if backend supports per-document view URL endpoint).
 * GET .../ems-lookup/{safetyId}/ems-access/documents/{documentId}/view-url with X-EMS-Access-Token.
 * Response expected: { success, data: { url: "..." } } or { url: "..." }.
 * @param {string} safetyId - Safety ID code
 * @param {string} documentId - Document UUID
 * @param {string} token - Documents token
 * @returns {Promise<{ viewUrl: string|null, notFound?: boolean, apiError?: string }>}
 */
export async function fetchEmsDocumentViewUrl(safetyId, documentId, token) {
  const apiBase = getEmsApiBase();
  if (!apiBase || !(safetyId || "").trim() || !(documentId || "").trim()) return { viewUrl: null, apiError: "config" };
  const code = (safetyId || "").trim().toUpperCase();
  const url = `${apiBase}/ems-lookup/${encodeURIComponent(code)}/ems-access/documents/${encodeURIComponent(documentId)}/view-url`;
  try {
    const res = await fetch(url, { headers: emsHeaders(token) });
    if (res.status === 404) return { viewUrl: null, notFound: true };
    if (res.status === 403) return { viewUrl: null, apiError: "forbidden" };
    if (!res.ok) return { viewUrl: null, apiError: "http" };
    const data = await res.json().catch(() => null);
    const viewUrl =
      data?.data?.url ?? data?.url ?? data?.data?.viewUrl ?? data?.viewUrl ?? null;
    return { viewUrl: viewUrl && typeof viewUrl === "string" ? viewUrl.trim() : null };
  } catch (e) {
    console.warn("[EMS API] fetch document view URL error:", e?.message ?? e);
    return { viewUrl: null, apiError: "network" };
  }
}

/**
 * Fetch documents list for EMS (after PIN validated).
 * Backend returns only items with Show to EMS = true.
 * @param {string} safetyId - Safety ID code
 * @param {string} token - From validateEmsPin with type "documents"
 * @returns {Promise<{ documents: Array, apiError?: string, status?: number }>}
 */
export async function fetchEmsDocuments(safetyId, token) {
  const apiBase = getEmsApiBase();
  if (!apiBase || !(safetyId || "").trim()) return { documents: [], apiError: "config" };
  const code = (safetyId || "").trim().toUpperCase();
  const url = `${apiBase}/ems-lookup/${encodeURIComponent(code)}/ems-access/documents`;
  try {
    const res = await fetch(url, { headers: emsHeaders(token) });
    if (res.status === 403) return { documents: [], apiError: "forbidden", status: 403 };
    if (!res.ok) return { documents: [], apiError: "http", status: res.status };
    const data = await res.json().catch(() => null);
    const list = data?.data?.documents ?? [];
    return { documents: Array.isArray(list) ? list : [] };
  } catch (e) {
    console.warn("[EMS API] fetch documents error:", e?.message ?? e);
    return { documents: [], apiError: "network" };
  }
}

/**
 * Fetch notes list for EMS (after PIN validated).
 * Backend returns only items with Show to EMS = true.
 * @param {string} safetyId - Safety ID code
 * @param {string} token - From validateEmsPin with type "notes"
 * @returns {Promise<{ notes: Array, apiError?: string, status?: number }>}
 */
export async function fetchEmsNotes(safetyId, token) {
  const apiBase = getEmsApiBase();
  if (!apiBase || !(safetyId || "").trim()) return { notes: [], apiError: "config" };
  const code = (safetyId || "").trim().toUpperCase();
  const url = `${apiBase}/ems-lookup/${encodeURIComponent(code)}/ems-access/notes`;
  try {
    const res = await fetch(url, { headers: emsHeaders(token) });
    if (res.status === 403) return { notes: [], apiError: "forbidden", status: 403 };
    if (!res.ok) return { notes: [], apiError: "http", status: res.status };
    const data = await res.json().catch(() => null);
    const list = data?.data?.notes ?? [];
    return { notes: Array.isArray(list) ? list : [] };
  } catch (e) {
    console.warn("[EMS API] fetch notes error:", e?.message ?? e);
    return { notes: [], apiError: "network" };
  }
}
